import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Card } from 'react-bootstrap';
import { twoFactorAPI } from '../api/twoFactorAPI';

function TwoFactorSetup({ show, onHide, onComplete }) {
    const [setupData, setSetupData] = useState(null);
    const [token, setToken] = useState('');
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1); // 1: QR Code, 2: Verification
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show && step === 1) {
            loadSetupData();
        }
    }, [show, step]);

    const loadSetupData = async () => {
        try {
            setLoading(true);
            const data = await twoFactorAPI.setup();
            setSetupData(data);
            setError(null);
        } catch (error) {
            setError('Failed to load 2FA setup data');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        if (token.length !== 6) {
            setError('Please enter a 6-digit code');
            return;
        }
        
        try {
            setLoading(true);
            await twoFactorAPI.verifySetup(token);
            onComplete();
            onHide();
        } catch (error) {
            setError('Invalid verification code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setToken('');
        setError(null);
        setSetupData(null);
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fas fa-shield-alt me-2"></i>
                    Setup Two-Factor Authentication
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {step === 1 && (
                    <div>
                        <h5>Step 1: Scan QR Code</h5>
                        <p className="text-muted">
                            Use your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.) to scan this QR code:
                        </p>
                        
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Setting up 2FA...</p>
                            </div>
                        ) : setupData ? (
                            <div className="text-center mb-3">
                                <img 
                                    src={setupData.qr_code} 
                                    alt="2FA QR Code" 
                                    style={{ maxWidth: '200px', border: '1px solid #dee2e6', borderRadius: '8px' }} 
                                />
                            </div>
                        ) : null}
                        
                        {setupData && (
                            <Card className="mb-3">
                                <Card.Header>
                                    <h6 className="mb-0">Manual Entry (if QR code doesn&apos;t work)</h6>
                                </Card.Header>
                                <Card.Body>
                                    <p className="mb-2"><strong>Secret Key:</strong></p>
                                    <code className="d-block p-2 bg-light rounded">{setupData.secret_key}</code>
                                </Card.Body>
                            </Card>
                        )}
                        
                        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                        
                        <div className="text-center">
                            <Button 
                                onClick={() => setStep(2)} 
                                variant="primary"
                                disabled={!setupData || loading}
                            >
                                I&apos;ve scanned the code
                            </Button>
                        </div>
                    </div>
                )}
                
                {step === 2 && (
                    <div>
                        <h5>Step 2: Verify Setup</h5>
                        <p className="text-muted">
                            Enter the 6-digit code from your authenticator app to complete the setup:
                        </p>
                        
                        <Form onSubmit={handleVerify}>
                            <Form.Group className="mb-3">
                                <Form.Label>Verification Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={token}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                        setToken(value);
                                        setError(null);
                                    }}
                                    placeholder="Enter 6-digit code"
                                    maxLength="6"
                                    className="text-center"
                                    style={{ fontSize: '1.5rem', letterSpacing: '0.5rem' }}
                                    autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                            </Form.Group>
                            
                            {error && <Alert variant="danger">{error}</Alert>}
                            
                            <div className="d-flex justify-content-between">
                                <Button 
                                    variant="secondary" 
                                    onClick={() => setStep(1)}
                                    disabled={loading}
                                >
                                    Back
                                </Button>
                                <Button 
                                    type="submit" 
                                    variant="primary"
                                    disabled={token.length !== 6 || loading}
                                >
                                    {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
                                </Button>
                            </div>
                        </Form>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default TwoFactorSetup;
